import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Principal } from "@dfinity/principal";
import { useAdmin } from "../contexts/AdminContext";
import { StatusBadge } from "../components/StatusBadge";
import {
  getIndexCanisterId,
  getFrontendCanisterId,
  getReportsCanisterId,
} from "../lib/actors";
import type { ShopRecord, PendingShop, CanisterStatus } from "../lib/types";
import { formatCycles, daysLeft } from "../lib/utils";

type Stats = {
  shops: number;
  pending: number;
  awaitingApproval: number;
} | null;

type InfraStatus = {
  index: CanisterStatus | null;
  frontend: CanisterStatus | null;
  reports: CanisterStatus | null;
};

type InfraErrors = {
  index: boolean;
  frontend: boolean;
  reports: boolean;
};

const INFRA_CANISTERS = {
  index: getIndexCanisterId(),
  frontend: getFrontendCanisterId(),
  reports: getReportsCanisterId(),
} as const;

export function HomePage() {
  const { actor, isInstaller } = useAdmin();
  const [stats, setStats] = useState<Stats>(null);
  const [infra, setInfra] = useState<InfraStatus>({
    index: null,
    frontend: null,
    reports: null,
  });
  const [infraErrors, setInfraErrors] = useState<InfraErrors>({
    index: false,
    frontend: false,
    reports: false,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!actor) return;
    (async () => {
      try {
        const [shopsRes, pendingRes] = await Promise.all([
          actor.listShops() as Promise<{ ok?: ShopRecord[]; err?: unknown }>,
          actor.listPendingShops() as Promise<{
            ok?: PendingShop[];
            err?: unknown;
          }>,
        ]);
        const shops = "ok" in shopsRes ? shopsRes.ok!.length : 0;
        const pending = "ok" in pendingRes ? pendingRes.ok!.length : 0;
        const awaitingApproval =
          "ok" in pendingRes
            ? pendingRes.ok!.filter((p) => "AwaitingApproval" in p.status)
                .length
            : 0;
        setStats({ shops, pending, awaitingApproval });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load stats");
      }
    })();
  }, [actor]);

  useEffect(() => {
    if (!actor || !isInstaller) return;
    (async () => {
      const getStatusThroughIndex = async (
        canisterId: string,
      ): Promise<CanisterStatus> => {
        const result = (await actor.getCanisterStatus(
          Principal.fromText(canisterId),
        )) as { ok?: CanisterStatus; err?: unknown };
        if ("ok" in result && result.ok) return result.ok;
        throw new Error("Index could not read canister status");
      };

      const [indexRes, frontendRes, reportsRes] = await Promise.allSettled([
        getStatusThroughIndex(INFRA_CANISTERS.index),
        getStatusThroughIndex(INFRA_CANISTERS.frontend),
        getStatusThroughIndex(INFRA_CANISTERS.reports),
      ]);
      const index = indexRes.status === "fulfilled" ? indexRes.value : null;
      const frontend =
        frontendRes.status === "fulfilled" ? frontendRes.value : null;
      const reports =
        reportsRes.status === "fulfilled" ? reportsRes.value : null;
      setInfra({ index, frontend, reports });
      setInfraErrors({
        index: indexRes.status === "rejected",
        frontend: frontendRes.status === "rejected",
        reports: reportsRes.status === "rejected",
      });
    })();
  }, [actor, isInstaller]);

  return (
    <div>
      <h1 style={{ fontSize: 24, marginBottom: 8 }}>Dashboard</h1>
      <p
        style={{ color: "var(--color-muted)", marginBottom: 32, fontSize: 14 }}
      >
        TillFlo platform overview
      </p>

      {error && (
        <p style={{ color: "var(--color-red)", marginBottom: 24 }}>{error}</p>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 16,
          marginBottom: 40,
        }}
      >
        <StatCard
          label="Total Shops"
          value={stats ? String(stats.shops) : "—"}
          to="/shops"
          accent
        />
        <StatCard
          label="Pending Shops"
          value={stats ? String(stats.pending) : "—"}
          to="/pending"
        />
        <StatCard
          label="Awaiting Approval"
          value={stats ? String(stats.awaitingApproval) : "—"}
          to="/pending"
          highlight={stats ? stats.awaitingApproval > 0 : false}
        />
      </div>

      {isInstaller && (
        <>
          <h2
            style={{
              fontSize: 14,
              color: "var(--color-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 16,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Infrastructure Health
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 16,
              marginBottom: 40,
            }}
          >
            <InfraCard
              label="Index Canister"
              status={infra.index}
              id={INFRA_CANISTERS.index}
              statusError={infraErrors.index}
            />
            <InfraCard
              label="Frontend Canister"
              status={infra.frontend}
              id={INFRA_CANISTERS.frontend}
              statusError={infraErrors.frontend}
            />
            <InfraCard
              label="Reports Canister"
              status={infra.reports}
              id={INFRA_CANISTERS.reports}
              statusError={infraErrors.reports}
            />
          </div>
        </>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        <QuickLink to="/shops" label="View Shops" />
        <QuickLink to="/pending" label="Review Pending" />
        <QuickLink to="/admins" label="Manage Admins" />
      </div>
    </div>
  );
}

function InfraCard({
  label,
  status,
  id,
  statusError,
}: {
  label: string;
  status: CanisterStatus | null;
  id: string;
  statusError: boolean;
}) {
  const days = status
    ? daysLeft(status.cycles, status.idleCyclesBurnedPerDay)
    : null;
  return (
    <div className="card">
      <h3
        style={{
          marginBottom: 16,
          fontSize: 14,
          color: "var(--color-muted)",
          fontFamily: "'DM Sans', sans-serif",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </h3>
      <p
        style={{
          fontSize: 11,
          color: "var(--color-muted)",
          fontFamily: "'DM Mono', monospace",
          marginBottom: 12,
          wordBreak: "break-all",
        }}
      >
        {id}
      </p>
      {status && days !== null ? (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <span
              style={{
                fontSize: 22,
                fontFamily: "'DM Mono', monospace",
                color: "var(--color-text)",
              }}
            >
              {formatCycles(status.cycles)}
            </span>
            <StatusBadge days={days} />
          </div>
          <StatRow
            label="Burn / Day"
            value={formatCycles(status.idleCyclesBurnedPerDay)}
          />
          <StatRow
            label="Days Left"
            value={isFinite(days) ? String(days) : "∞"}
          />
          <StatRow
            label="Memory"
            value={`${(Number(status.memorySize) / 1024 / 1024).toFixed(1)} MB`}
          />
        </>
      ) : statusError ? (
        <p style={{ color: "var(--color-red)", fontSize: 13 }}>
          Status unavailable
        </p>
      ) : (
        <p style={{ color: "var(--color-muted)", fontSize: 13 }}>Loading…</p>
      )}
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 0",
        borderBottom: "1px solid var(--color-border)",
      }}
    >
      <span style={{ fontSize: 13, color: "var(--color-muted)" }}>{label}</span>
      <span
        style={{
          fontSize: 14,
          fontFamily: "'DM Mono', monospace",
          color: "var(--color-text)",
          fontWeight: 500,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function StatCard({
  label,
  value,
  to,
  accent,
  highlight,
}: {
  label: string;
  value: string;
  to: string;
  accent?: boolean;
  highlight?: boolean;
}) {
  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      <div className="card" style={{ cursor: "pointer" }}>
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            fontFamily: "'DM Mono', monospace",
            color: accent
              ? "var(--color-accent)"
              : highlight
                ? "var(--color-yellow)"
                : "var(--color-text)",
            marginBottom: 4,
          }}
        >
          {value}
        </div>
        <div style={{ fontSize: 13, color: "var(--color-muted)" }}>{label}</div>
      </div>
    </Link>
  );
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to}>
      <div
        className="card"
        style={{
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
        }}
      >
        {label} <span style={{ color: "var(--color-accent)" }}>→</span>
      </div>
    </Link>
  );
}
